import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { maineCoonContent, images, galleryImages } from '@/data/mock';
import { Cat, Heart, Shield, Utensils, Activity, Stethoscope } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const MaineCoonPage = () => {
  const maineCoonImages = galleryImages.filter(img => img.category === 'maine-coon');

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium mb-4">
                Nos Maine Coons
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--text-primary)] mb-6">
                {maineCoonContent.title}
              </h1>
              <p className="text-xl text-[var(--color-gold)] font-serif italic mb-6">
                {maineCoonContent.subtitle}
              </p>
              <div className="w-24 h-px bg-[var(--color-gold)] mb-8"></div>
              <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
                {maineCoonContent.description}
              </p>
            </div>
            <div className="relative">
              <div className="cat-card rounded-2xl overflow-hidden">
                <img
                  src={images.maineCoon1}
                  alt="Maine Coon - Maison Bengal & Coon"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[var(--color-gold)] rounded-full opacity-20 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Characteristics */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-4">
              Caractéristiques du Maine Coon
            </h2>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {maineCoonContent.characteristics.map((char, index) => {
              const icons = [Cat, Heart, Activity, Shield];
              const Icon = icons[index];
              return (
                <div key={index} className="card-elegant p-8 rounded-xl hover-lift">
                  <div className="feature-icon mb-5">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif text-xl text-[var(--text-primary)] mb-3">
                    {char.title}
                  </h3>
                  <p className="text-[var(--text-subtle)] leading-relaxed">
                    {char.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full Description */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            {maineCoonContent.fullText.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[var(--text-secondary)] leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Health & Nutrition Accordions */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-4">
              Santé & Alimentation
            </h2>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto"></div>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {/* Health Section */}
            <AccordionItem value="health" className="border border-[var(--color-beige)] rounded-xl px-6 bg-white">
              <AccordionTrigger className="hover:no-underline py-6">
                <div className="flex items-center gap-4">
                  <div className="feature-icon !w-12 !h-12">
                    <Stethoscope className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-serif text-xl text-[var(--text-primary)]">
                      {maineCoonContent.health.title}
                    </h3>
                    <p className="text-sm text-[var(--text-subtle)] font-normal">
                      Tests ADN & suivi vétérinaire
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                  {maineCoonContent.health.description}
                </p>
                <div className="bg-[var(--surface-cream)] rounded-xl p-6">
                  <h4 className="font-medium text-[var(--text-primary)] mb-4">
                    Nos engagements santé
                  </h4>
                  <ul className="space-y-3">
                    {maineCoonContent.health.tests.map((test, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-2 h-2 bg-[var(--color-gold)] rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-[var(--text-secondary)]">{test}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Nutrition Section */}
            <AccordionItem value="nutrition" className="border border-[var(--color-beige)] rounded-xl px-6 bg-white">
              <AccordionTrigger className="hover:no-underline py-6">
                <div className="flex items-center gap-4">
                  <div className="feature-icon !w-12 !h-12">
                    <Utensils className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-serif text-xl text-[var(--text-primary)]">
                      {maineCoonContent.nutrition.title}
                    </h3>
                    <p className="text-sm text-[var(--text-subtle)] font-normal">
                      Partenariat Royal Canin
                    </p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                  {maineCoonContent.nutrition.description}
                </p>
                <div className="bg-[var(--surface-cream)] rounded-xl p-6">
                  <h4 className="font-medium text-[var(--text-primary)] mb-4">
                    Notre approche nutritionnelle
                  </h4>
                  <ul className="space-y-3">
                    {maineCoonContent.nutrition.points.map((point, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-2 h-2 bg-[var(--color-gold)] rounded-full mt-2 flex-shrink-0"></span>
                        <span className="text-[var(--text-secondary)]">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-4">
              Nos Maine Coons
            </h2>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto"></div>
            <p className="text-[var(--text-subtle)] mt-4">
              Photos libres de droit en attendant nos propres photos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {maineCoonImages.map((image) => (
              <div key={image.id} className="gallery-image aspect-square rounded-xl overflow-hidden">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MaineCoonPage;
